import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useId, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IGreetingFormData } from "../../types";
import { greetingValidationSchema } from "../../validations";
import { INITIAL_GREETINGS } from "../configs";
import { validateGreetingData } from "../utils/validateGreetings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseGreetingSettingsProps {
    characterId: string;
}

export type GreetingFormWithFileFields = IGreetingFormData &
    Record<string, NonNullable<unknown> | undefined> & {
        [key: `audioFile_${number}_${number}_${number}`]: File | null;
    };

const DEFAULT_GREETING_DATA: GreetingFormWithFileFields = {
    greetings: {
        enabled: INITIAL_GREETINGS.enabled,
        data: INITIAL_GREETINGS.data,
    },
};

export const useGreetingSettings = ({ characterId }: UseGreetingSettingsProps) => {
    const { t } = useTranslation();
    const rawId = useId();
    const keyPrefix = rawId.replace(/^:/, ""); // 去除開頭的冒號
    const { kioskSetting, isUpdatingKioskSetting, updateGreetings } = useKioskSettings({
        characterId,
    });
    const [unsavedCards, setUnsavedCards] = useState<Set<number>>(new Set());
    const [uploadingCards, setUploadingCards] = useState<Set<number>>(new Set());

    const methods = useForm<GreetingFormWithFileFields>({
        mode: "all",
        defaultValues: DEFAULT_GREETING_DATA,
        resolver: yupResolver(greetingValidationSchema()),
    });

    const {
        fields: greetingFields,
        append: appendGreeting,
        remove: removeGreeting,
    } = useFieldArray({
        control: methods.control,
        name: "greetings.data" as never,
    });

    const getUsedLanguagesByOthers = (currentIndex: number) => {
        return greetingFields
            .filter((_, index) => index !== currentIndex)
            .flatMap((greeting) => (greeting as unknown as { languages: string[] }).languages || []);
    };

    const markCardAsChanged = (cardIndex: number) => {
        setUnsavedCards((prev) => new Set(prev).add(cardIndex));
    };

    const clearCardUnsaved = (cardIndex: number) => {
        setUnsavedCards((prev) => {
            const newSet = new Set(prev);
            newSet.delete(cardIndex);
            return newSet;
        });
    };

    const markCardAsUploading = (cardIndex: number) => {
        setUploadingCards((prev) => new Set(prev).add(cardIndex));
    };

    const clearCardUploading = (cardIndex: number) => {
        setUploadingCards((prev) => {
            const newSet = new Set(prev);
            newSet.delete(cardIndex);
            return newSet;
        });
    };

    const generateSentenceKey = useCallback(() => {
        return `${keyPrefix}${Date.now()}-${Math.random().toString(36).substring(2, 4)}`;
    }, [keyPrefix]);

    const handleAddDialogGroup = () => {
        const newIndex = greetingFields.length;
        appendGreeting({
            languages: [],
            dialogues: [
                {
                    sentences: [
                        {
                            key: generateSentenceKey(),
                            content: "",
                            face: "",
                            action: "",
                            audio_url: "",
                            updated_time: new Date().toISOString(),
                        },
                    ],
                },
            ],
        });
        markCardAsChanged(newIndex);
    };

    const handleRemoveGreetingGroup = (greetingIndex: number) => {
        const confirmMessage = `${t("APPWIDGET.GREETINGS_CONFIRM_DELETE") || "確定要刪除對話組嗎"}`;

        if (window.confirm(confirmMessage)) {
            removeGreeting(greetingIndex);
        }
    };

    const handleAddSentence = (greetingIndex: number, dialogIndex: number) => {
        const currentSentences =
            methods.getValues(`greetings.data.${greetingIndex}.dialogues.${dialogIndex}.sentences`) || [];
        const newSentence = {
            key: generateSentenceKey(),
            content: "",
            face: "",
            action: "",
            audio_url: "",
            updated_time: new Date().toISOString(),
        };

        methods.setValue(`greetings.data.${greetingIndex}.dialogues.${dialogIndex}.sentences`, [
            ...currentSentences,
            newSentence,
        ]);
    };

    const handleRemoveSentence = (greetingIndex: number, dialogIndex: number, sentenceIndex: number) => {
        const currentSentences =
            methods.getValues(`greetings.data.${greetingIndex}.dialogues.${dialogIndex}.sentences`) || [];
        const updatedSentences = currentSentences.filter((_, index) => index !== sentenceIndex);

        methods.setValue(`greetings.data.${greetingIndex}.dialogues.${dialogIndex}.sentences`, updatedSentences);
    };

    const handleUpdateSentenceTimestamp = (greetingIndex: number, dialogIndex: number, sentenceIndex: number) => {
        const fieldPath = `greetings.data.${greetingIndex}.dialogues.${dialogIndex}.sentences.${sentenceIndex}.updated_time`;
        methods.setValue(fieldPath as never, new Date().toISOString() as never);
        markCardAsChanged(greetingIndex);
    };

    const handleSaveAll = async () => {
        try {
            const isValid = await methods.trigger();
            if (!isValid) {
                customToast.warn(t("APPWIDGET.GREETINGS_SENTENCE_WARN") || "必須填寫語言、內容與音檔連結");
                return;
            }

            const currentData = methods.getValues();

            const invalidCards: Array<{ index: number; reason: string }> = [];
            const validGreetings = currentData.greetings.data.filter((g, idx) => {
                const cardNumber = idx + 1;

                if (!g.languages || g.languages.length === 0) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_LANGUAGE", { index: cardNumber }) ||
                            `對話組-${cardNumber}：未選擇語言`,
                    });
                    return false;
                }

                const hasValidContent = g.dialogues.some((d) => d.sentences.some((s) => s.content?.trim()));

                if (!hasValidContent) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_CONTENT", { index: cardNumber }) ||
                            `對話組-${cardNumber}：招呼詞內容為空`,
                    });
                    return false;
                }

                const hasInvalidAudioUrl = g.dialogues.some((d) =>
                    d.sentences.some((s) => s.content?.trim() && !s.audio_url?.trim())
                );

                if (hasInvalidAudioUrl) {
                    invalidCards.push({
                        index: idx,
                        reason:
                            t("APPWIDGET.GREETINGS_NO_AUDIO_URL", { index: cardNumber }) ||
                            `對話組-${cardNumber}：音檔連結為空`,
                    });
                    return false;
                }

                return true;
            });

            if (invalidCards.length > 0) {
                const errorMessages = invalidCards.map((card) => card.reason).join("；");
                customToast.warn(errorMessages);
                return;
            }

            const validationResult = validateGreetingData(validGreetings);
            if (!validationResult.isValid) {
                customToast.error(validationResult.errorMessage);
                return;
            }

            const apiPayload = {
                enabled: currentData.greetings.enabled,
                data: validGreetings.map((g) => ({
                    languages: g.languages,
                    dialogues: g.dialogues.map((dialogue) => ({
                        sentences: dialogue.sentences.map((sentence) => ({
                            key: sentence.key,
                            content: sentence.content,
                            face: sentence.face || "",
                            action: sentence.action || "",
                            audio_url: sentence.audio_url,
                            updated_time: sentence.updated_time || new Date().toISOString(),
                        })),
                    })),
                })),
            };

            await updateGreetings(apiPayload);

            setUnsavedCards(new Set());
            customToast.success(t("APPWIDGET.GREETINGS_SENTENCE_SAVED_SUCCESS") || "招呼詞已保存");
        } catch (error) {
            customToast.error(t("APPWIDGET.GREETINGS_SENTENCE_ERROR") || "無法保存招呼詞，請重試");
            throw error;
        }
    };

    useTabGuard("greeting", unsavedCards.size > 0, handleSaveAll);

    useEffect(() => {
        const greetingsRaw = kioskSetting?.data?.native?.greetings;

        if (greetingsRaw) {
            const { enabled = true, data: greetingsData = [] } = Array.isArray(greetingsRaw)
                ? { enabled: true, data: greetingsRaw }
                : greetingsRaw;

            const formData: IGreetingFormData = {
                greetings: {
                    enabled,
                    data: greetingsData.map((greeting) => ({
                        languages: greeting.languages || [],
                        dialogues:
                            greeting.dialogues && greeting.dialogues.length > 0
                                ? greeting.dialogues.map((dialogue) => ({
                                      sentences: dialogue.sentences.map((sentence) => ({
                                          ...sentence,
                                          updated_time: sentence.updated_time || new Date().toISOString(),
                                      })),
                                  }))
                                : [
                                      {
                                          sentences: [
                                              {
                                                  key: generateSentenceKey(),
                                                  content: "",
                                                  face: "",
                                                  action: "",
                                                  audio_url: "",
                                                  updated_time: new Date().toISOString(),
                                              },
                                          ],
                                      },
                                  ],
                    })),
                },
            };

            methods.reset(formData as GreetingFormWithFileFields);
        }
    }, [kioskSetting, methods, generateSentenceKey]);

    return {
        methods,
        greetingFields,
        getUsedLanguagesByOthers,
        updateGreetings,
        isUpdatingKioskSetting,
        handleAddDialogGroup,
        handleRemoveGreetingGroup,
        handleAddSentence,
        handleRemoveSentence,
        handleUpdateSentenceTimestamp,
        handleSaveAll,
        unsavedCards,
        markCardAsChanged,
        clearCardUnsaved,
        uploadingCards,
        markCardAsUploading,
        clearCardUploading,
    };
};
